import React, { useState } from 'react';
import { ChatCircle, Coffee, Handshake, User, ArrowBendUpLeft, Paperclip, Trash } from '@phosphor-icons/react';
import { format, parseISO } from 'date-fns';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatTime = (dateStr) => {
  try {
    const date = parseISO(dateStr);
    const today = new Date();
    if (date.toDateString() === today.toDateString()) return format(date, 'HH:mm');
    return format(date, 'dd/MM HH:mm');
  } catch { return ''; }
};

const ChannelIcon = ({ channelId }) => {
  if (channelId === 'detente') return <Coffee size={18} style={{ color: '#E65100' }} />;
  if (channelId === 'entraide') return <Handshake size={18} style={{ color: '#2E7D32' }} />;
  return <User size={18} style={{ color: '#6A1B9A' }} />;
};

const MessageBubble = ({ msg, isOwn, onReply, onDelete }) => {
  const [showActions, setShowActions] = useState(false);
  const bubbleColor = isOwn ? (msg.sender_color || '#002FA7') : '#F4F5F7';
  const textColor = isOwn ? 'white' : '#0A0B10';
  const nameColor = msg.sender_color || '#002FA7';

  const handleDelete = async () => {
    if (!window.confirm('Supprimer ce message ?')) return;
    try {
      await axios.delete(`${API_URL}/api/chat/messages/${msg.id}`, { withCredentials: true });
      onDelete(msg.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur suppression');
    }
  };

  return (
    <div style={{ display: 'flex', justifyContent: isOwn ? 'flex-end' : 'flex-start', marginBottom: '8px', padding: '0 4px' }}>
      <div style={{ maxWidth: '80%' }}
        onMouseEnter={() => setShowActions(true)} onMouseLeave={() => setShowActions(false)}
        onTouchStart={() => setShowActions(true)}>
        {msg.reply_to_content && (
          <div style={{ fontSize: '0.72rem', color: '#5E5F66', padding: '4px 12px', borderLeft: '2px solid #90CAF9', marginBottom: '3px', backgroundColor: '#F8F9FF', borderRadius: '4px' }}>
            <strong>{msg.reply_to_name}</strong>: {msg.reply_to_content}
          </div>
        )}
        <div style={{
          backgroundColor: bubbleColor, color: textColor,
          padding: '10px 14px',
          borderRadius: isOwn ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
          position: 'relative', minWidth: '60px'
        }}>
          {!isOwn && <div style={{ fontWeight: '700', fontSize: '0.73rem', marginBottom: '3px', color: nameColor }}>{msg.sender_name}</div>}
          {msg.file_url ? (
            msg.file_type === 'image' ? (
              <a href={`${API_URL}${msg.file_url}?t=${msg.id}`} target="_blank" rel="noreferrer">
                <img
                  src={`${API_URL}${msg.file_url}?t=${msg.id}`}
                  alt={msg.file_name}
                  onError={(e) => { e.currentTarget.style.display = 'none'; const fb = e.currentTarget.nextElementSibling; if (fb) fb.style.display = 'flex'; }}
                  style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: '8px', cursor: 'pointer', display: 'block' }}
                />
                <span style={{ display: 'none', alignItems: 'center', gap: '6px', fontSize: '0.8rem', color: '#C62828' }}>
                  <Paperclip size={12} /> Image indisponible ({msg.file_name})
                </span>
              </a>
            ) : (
              <a href={`${API_URL}${msg.file_url}?t=${msg.id}`} target="_blank" rel="noreferrer" style={{ display: 'flex', alignItems: 'center', gap: '6px', color: isOwn ? 'white' : '#1565C0', textDecoration: 'underline', fontSize: '0.85rem', wordBreak: 'break-all' }}>
                <Paperclip size={14} /> {msg.file_name || 'piece jointe'}
              </a>
            )
          ) : (
            <div style={{ wordBreak: 'break-word', fontSize: '0.9rem', lineHeight: 1.45 }}>{msg.content}</div>
          )}
          <div style={{ fontSize: '0.65rem', opacity: 0.55, marginTop: '3px', textAlign: 'right' }}>{formatTime(msg.created_at)}</div>

          {/* Actions */}
          {showActions && (
            <div style={{
              position: 'absolute', top: '-28px',
              [isOwn ? 'left' : 'right']: '0',
              display: 'flex', gap: '2px', backgroundColor: 'white',
              borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              padding: '3px', zIndex: 10
            }}>
              <button onClick={() => onReply(msg)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '5px', borderRadius: '6px', color: '#1565C0' }} title="Repondre">
                <ArrowBendUpLeft size={14} />
              </button>
              {isOwn && (
                <button onClick={handleDelete} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '5px', borderRadius: '6px', color: '#C62828' }} title="Supprimer" data-testid={`delete-msg-${msg.id}`}>
                  <Trash size={14} />
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export const ChatMessages = ({ messages, activeChannel, activeChInfo, userId, typingUsers, setReplyTo, messagesEndRef, onDeleteMessage, isMobile, onBackToSidebar }) => (
  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
    {/* Channel header */}
    <div style={{
      padding: isMobile ? '10px 12px' : '10px 16px',
      borderBottom: '1px solid #E2E2E6',
      fontWeight: '700', fontSize: '0.95rem',
      display: 'flex', alignItems: 'center', gap: '8px',
      backgroundColor: 'white', flexShrink: 0
    }}>
      {isMobile && (
        <button onClick={onBackToSidebar} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: '#1565C0' }} data-testid="back-to-channels">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6"/></svg>
        </button>
      )}
      <ChannelIcon channelId={activeChannel} />
      <span>{activeChInfo?.name || activeChannel}</span>
    </div>

    {/* Messages area */}
    <div style={{ flex: 1, overflowY: 'auto', padding: isMobile ? '10px 6px' : '16px 12px', WebkitOverflowScrolling: 'touch' }}>
      {messages.length > 0 ? messages.map((msg) => (
        <MessageBubble key={msg.id} msg={msg} isOwn={msg.sender_id === userId} onReply={setReplyTo} onDelete={onDeleteMessage} />
      )) : (
        <div style={{ textAlign: 'center', padding: '40px 16px', color: '#9E9E9E' }}>
          <ChatCircle size={40} style={{ marginBottom: '8px', opacity: 0.4 }} />
          <p style={{ fontSize: '0.85rem' }}>Aucun message dans ce canal</p>
        </div>
      )}
      <div ref={messagesEndRef} />
    </div>

    {/* Typing indicator */}
    {Object.keys(typingUsers).length > 0 && (
      <div style={{ padding: '4px 16px', fontSize: '0.75rem', color: '#5E5F66', fontStyle: 'italic', borderTop: '1px solid #F4F5F7', flexShrink: 0 }}>
        {Object.values(typingUsers).map(u => u.name).join(', ')} ecrit...
      </div>
    )}
  </div>
);
